import { MeetingTranscriptSegmentDto } from '@interfaces/meetings/responses/meeting.response';

/**
 * Transcrição ligada ao vídeo na tela de detalhe (card 3162): qual fala está tocando, onde o termo
 * buscado aparece e como destacar. Funções puras, com spec isolado.
 */

/** Um pedaço do texto, destacado ou não. */
export interface ParteDoTexto {
  texto: string;
  destaque: boolean;
}

/** Sem acento e em minúsculas: "Reunião" acha "reuniao" e o contrário. */
export function normalizar(texto: string | null | undefined): string {
  return (texto ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLocaleLowerCase('pt-BR');
}

/**
 * A fala que está tocando: a última que começou até o tempo atual. Antes da primeira, nenhuma (-1).
 * Fala sem `startSeconds` não entra na conta. Busca binária: a transcrição de uma reunião longa tem
 * milhares de falas, e o `timeupdate` do vídeo dispara várias vezes por segundo.
 */
export function indiceDaFalaAtual(
  falas: readonly MeetingTranscriptSegmentDto[],
  segundos: number
): number {
  let inicio = 0;
  let fim = falas.length - 1;
  let achada = -1;

  while (inicio <= fim) {
    const meio = (inicio + fim) >> 1;
    const comeco = falas[meio].startSeconds;
    if (comeco !== null && comeco !== undefined && comeco <= segundos) {
      achada = meio;
      inicio = meio + 1;
    } else {
      fim = meio - 1;
    }
  }

  return achada;
}

/**
 * Índices das falas em que o termo aparece **no que foi dito**. O nome de quem fala fica de fora: a
 * busca por "assinar" achava "Tassinari" e contava uma fala sem nada destacado.
 */
export function falasComOTermo(
  falas: readonly MeetingTranscriptSegmentDto[],
  termo: string
): number[] {
  const busca = normalizar(termo).trim();
  if (!busca) return [];

  const achadas: number[] = [];
  falas.forEach((fala, i) => {
    if (normalizar(fala.text).includes(busca)) {
      achadas.push(i);
    }
  });
  return achadas;
}

/**
 * O texto partido nos trechos que batem com o termo, preservando a grafia original (a comparação é
 * sem acento, mas o que aparece na tela é o texto como veio).
 */
export function partesDestacadas(texto: string | null | undefined, termo: string): ParteDoTexto[] {
  const original = texto ?? '';
  const busca = normalizar(termo).trim();
  if (!busca || !original) return [{ texto: original, destaque: false }];

  // `normalize('NFD')` pode mudar o tamanho da string (letra + acento); normalizar letra a letra
  // mantém os índices do original.
  const comparavel = original
    .split('')
    .map(letra => normalizar(letra).charAt(0) || letra)
    .join('');

  const partes: ParteDoTexto[] = [];
  let posicao = 0;
  let achado = comparavel.indexOf(busca, posicao);
  while (achado !== -1) {
    if (achado > posicao) partes.push({ texto: original.slice(posicao, achado), destaque: false });
    partes.push({ texto: original.slice(achado, achado + busca.length), destaque: true });
    posicao = achado + busca.length;
    achado = comparavel.indexOf(busca, posicao);
  }
  if (posicao < original.length) partes.push({ texto: original.slice(posicao), destaque: false });

  return partes;
}
